import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFilter } from "@/partner/shared/components/Filter";
import { SimpleTableCard } from "@/partner/shared/components/SimpleTableCard";
import { formatNumber } from "@/shared/utils/formatNumber";
import { useAnalyticsViewsByUtmQuery } from "@/partner/entities/analytics";
import { DownloadReportButton } from "@/partner/features/reports";
import {
  AnalyticsWidgetType,
  ReportTypeType,
} from "@/types/graphql/partner.generated";

// `dimension` — обычная строковая уния (значения GraphQL-enum UtmDimension),
// а не сгенерированный `UtmDimensionType`: тот генерируется как настоящий TS
// enum (codegen.config.ts не задаёт `enumsAsTypes`), и строковый литерал
// вроде "SOURCE" не присваивается номинальному enum-типу без явного `as`.
// Тот же приём, что и `UtmField.tsx` (Task 18), которое тоже типизирует свой
// `dimension`-проп плоской строковой унией, а не enum-типом.
export type UtmDimensionValue =
  | "SOURCE"
  | "MEDIUM"
  | "CAMPAIGN"
  | "CONTENT"
  | "TERM";

interface AnalyticsViewsByUtmTableProps {
  dimension: UtmDimensionValue;
  title: string;
}

export const AnalyticsViewsByUtmTable: FC<AnalyticsViewsByUtmTableProps> = ({
  dimension,
  title,
}) => {
  const { t } = useTranslation();
  const { filter } = useFilter();
  const [limit, setLimit] = useState(10);

  const { data, loading, error } = useAnalyticsViewsByUtmQuery({
    variables: { filter: filter as never, dimension: dimension as never },
  });

  const rows = (data?.analyticsViews.viewsByUtm ?? [])
    .slice(0, limit)
    .map((row) => [row.value, formatNumber(row.views)]);

  return (
    <SimpleTableCard
      title={title}
      columns={[
        { name: t("analytics.views.columnUtmValue") },
        { name: t("analytics.views.columnViews") },
      ]}
      rows={rows}
      loading={loading}
      error={error}
      limit={limit}
      setLimit={setLimit}
      downloadButton={
        <DownloadReportButton
          variant="card"
          type={ReportTypeType.AnalyticsWidgetType}
          widget={AnalyticsWidgetType.ViewsByUtmType}
          filter={filter}
          variables={{ dimension }}
        />
      }
    />
  );
};
